"use client";

import * as React from "react";
import { createPortal, flushSync } from "react-dom";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import type { LexicalNode } from "lexical";
import {
  $createParagraphNode,
  $createTextNode,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
} from "lexical";
import {
  $deleteTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $getNodeTriplet,
  $getTableCellNodeFromLexicalNode,
  $getTableColumnIndexFromTableCellNode,
  $getTableRowIndexFromTableCellNode,
  $insertTableColumnAtSelection,
  $insertTableRowAtSelection,
  $isSimpleTable,
  $isTableNode,
  $isTableRowNode,
  $moveTableColumn,
  INSERT_TABLE_COMMAND,
  type TableCellNode,
  type TableRowNode,
} from "@lexical/table";

import { $moveTableRow } from "../../../kernel/helpers/moveTableRow";
import { DRAG_THRESHOLD_PX, MIN_COL_WIDTH, MIN_ROW_HEIGHT } from "./constants";
import { resetBodyDragChromeStyles } from "./dom-style";
import { snapTableAxisMenuAnchor } from "./menu-anchor-utils";
import { buildColumnResizeGuides } from "./column-resize-guides";
import { buildRowResizeGuides } from "./row-resize-guides";
import {
  buildColDragGhostTable,
  buildRowDragGhostTable,
  hideTableColumnCellContents,
  hideTableRowCellContents,
  restoreSourceContentHidden,
} from "./line-drag-dom";
import {
  colMoveToIndex,
  computeColDropLine,
  computeRowDropLine,
  resolveColLineDrag,
  resolveRowLineDrag,
  rowMoveToIndex,
  toDragBox,
} from "./line-drag-math";
import { $placeCaretAtStartOfTableCell, $selectTableCellAsRange } from "./lexical-table-cell-selection";
import { clamp } from "./number-utils";
import { pickTableBlockKeyAtViewportPoint } from "./pick-table-block-key";
import { clipFixedStyleToContainer, domRectsIntersect } from "./clip-layout-rect";
import {
  computeAddStripLayoutsFromTableRect,
  pointHitsTableAddColStrip,
  pointHitsTableAddRowStrip,
} from "./table-add-strip-visibility";
import { collectTableNodeKeys, readTableCtx, readTableCtxByCellKey, readTableRectByTableKey } from "./table-ctx";
import type {
  AxisFocus,
  DragBox,
  LineDragVisual,
  SourceContentHidden,
  TableAxisMenuAnchor,
  TableCtx,
  TableInteractionPluginProps,
} from "./types";
import { buildAxisFocusOverlayStyle, buildColGripStyle, buildRowGripStyle } from "./grip-overlay-styles";
import { TableInteractionPortals } from "./TableInteractionPortals";

export type { TableInteractionPluginProps } from "./types";

export function TableInteractionPlugin({ isEditable, containerRef }: TableInteractionPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [portalEl, setPortalEl] = React.useState<HTMLElement | null>(null);
  const [ctx, setCtx] = React.useState<TableCtx | null>(null);
  const [rowMenuOpen, setRowMenuOpen] = React.useState(false);
  const [colMenuOpen, setColMenuOpen] = React.useState(false);
  const [rowMenuAnchor, setRowMenuAnchor] = React.useState<TableAxisMenuAnchor | null>(null);
  const [colMenuAnchor, setColMenuAnchor] = React.useState<TableAxisMenuAnchor | null>(null);
  const [resizingColIndex, setResizingColIndex] = React.useState<number | null>(null);
  const [resizingRowIndex, setResizingRowIndex] = React.useState<number | null>(null);
  const [hoverTableKey, setHoverTableKey] = React.useState<string | null>(null);
  /** 与 `setHoverTableKey` 同步，供同一帧内的加条命中逻辑使用 */
  const hoverTableKeySyncRef = React.useRef<string | null>(null);
  /** 指针曾位于其单元格区域上的表，用于移入「加行/加列」条时仍解析到该表 */
  const stripStickyTableKeyRef = React.useRef<string | null>(null);
  const tableKeysCacheRef = React.useRef<string[]>([]);
  const [addStripPointer, setAddStripPointer] = React.useState<{
    tableKey: string | null;
    row: boolean;
    col: boolean;
  }>({ tableKey: null, row: false, col: false });
  const addStripPointerRef = React.useRef(addStripPointer);
  addStripPointerRef.current = addStripPointer;
  const lastPointerRef = React.useRef<{ x: number; y: number } | null>(null);
  const hoverRafRef = React.useRef(0);
  const [axisFocus, setAxisFocus] = React.useState<AxisFocus | null>(null);
  const [lineDragVisual, setLineDragVisual] = React.useState<LineDragVisual | null>(null);
  const ctxRef = React.useRef<TableCtx | null>(null);
  const lastTableCtxRef = React.useRef<TableCtx | null>(null);
  const axisFocusRef = React.useRef<AxisFocus | null>(null);
  const rowMenuOpenRef = React.useRef(false);
  const colMenuOpenRef = React.useRef(false);
  const scrollSyncRef = React.useRef<() => void>(() => {});
  const closeAllAxisMenusRef = React.useRef<() => void>(() => {});
  const tableWrapperScrollAttachedRef = React.useRef(new WeakSet<Element>());
  /** 滚动/视口变化时强制重渲染，以便条带/握把/轴高亮与 `containerRef` 裁剪及单元格聚焦可见性同步（`ctx` 引用可能不变）。 */
  const [floatingViewportNonce, bumpFloatingClipLayout] = React.useReducer((n: number) => n + 1, 0);

  const rowDragRef = React.useRef<{
    pointerId: number;
    startY: number;
    lastClientY: number;
    dragging: boolean;
    fromIndex: number;
    tableKey: string;
    cellKey?: string;
    rowBox?: DragBox;
    tableBox?: DragBox;
    minDy?: number;
    maxDy?: number;
    sourceTr?: HTMLTableRowElement | null;
    tableDom?: HTMLTableElement | null;
    sourceContentHidden?: SourceContentHidden[];
    raf?: number;
    pendingDy?: number;
  } | null>(null);
  const colDragRef = React.useRef<{
    pointerId: number;
    startX: number;
    lastClientX: number;
    dragging: boolean;
    fromIndex: number;
    tableKey: string;
    cellKey?: string;
    colBox?: DragBox;
    tableBox?: DragBox;
    minDx?: number;
    maxDx?: number;
    tableDom?: HTMLTableElement | null;
    colCount?: number;
    sourceContentHidden?: SourceContentHidden[];
    raf?: number;
    pendingDx?: number;
  } | null>(null);
  const resizeRef = React.useRef<{
    pointerId: number;
    colIndex: number;
    startX: number;
    startWidths: number[];
    tableKey: string;
  } | null>(null);
  const rowResizeRef = React.useRef<{
    pointerId: number;
    rowIndex: number;
    startY: number;
    startHeights: number[];
    tableKey: string;
  } | null>(null);
  const suppressRowClickRef = React.useRef(false);
  const suppressColClickRef = React.useRef(false);

  const rowLineDragGhostMountRef = React.useRef<HTMLDivElement | null>(null);
  const colLineDragGhostMountRef = React.useRef<HTMLDivElement | null>(null);
  const rowLineDragGhostTableRef = React.useRef<HTMLTableElement | null>(null);
  const colLineDragGhostTableRef = React.useRef<HTMLTableElement | null>(null);

  React.useEffect(() => {
    setPortalEl(document.body);
  }, []);

  React.useLayoutEffect(() => {
    if (lineDragVisual?.kind === "row") {
      const m = rowLineDragGhostMountRef.current;
      const t = rowLineDragGhostTableRef.current;
      if (m && t && m.firstChild !== t) m.replaceChildren(t);
    } else {
      rowLineDragGhostMountRef.current?.replaceChildren();
      if (lineDragVisual?.kind !== "col") rowLineDragGhostTableRef.current = null;
    }

    if (lineDragVisual?.kind === "col") {
      const m = colLineDragGhostMountRef.current;
      const t = colLineDragGhostTableRef.current;
      if (m && t && m.firstChild !== t) m.replaceChildren(t);
    } else {
      colLineDragGhostMountRef.current?.replaceChildren();
      if (lineDragVisual?.kind !== "row") colLineDragGhostTableRef.current = null;
    }
  }, [lineDragVisual]);

  const visualAppliedKeysRef = React.useRef<string[]>([]);

  const clearVisualFocusDom = React.useCallback(() => {
    for (const k of visualAppliedKeysRef.current) {
      const el = editor.getElementByKey(k);
      if (!el) continue;
      el.removeAttribute("data-rt-cell-focused");
    }
    visualAppliedKeysRef.current = [];
  }, [editor]);

  const applyVisualFocusDom = React.useCallback(
    (keys: string[]) => {
      clearVisualFocusDom();
      const applied: string[] = [];
      for (const k of keys) {
        const el = editor.getElementByKey(k);
        if (el) {
          el.setAttribute("data-rt-cell-focused", "true");
          applied.push(k);
        }
      }
      visualAppliedKeysRef.current = applied;
    },
    [clearVisualFocusDom, editor],
  );

  ctxRef.current = ctx;
  axisFocusRef.current = axisFocus;
  rowMenuOpenRef.current = rowMenuOpen;
  colMenuOpenRef.current = colMenuOpen;

  React.useEffect(() => {
    if (!isEditable) {
      clearVisualFocusDom();
      return;
    }
    if (axisFocus) return clearVisualFocusDom();
    if (lineDragVisual) return clearVisualFocusDom();
    if (!ctx) {
      clearVisualFocusDom();
      return;
    }
    const clip = containerRef.current?.getBoundingClientRect() ?? null;
    if (!clip || !domRectsIntersect(ctx.cellRect, clip)) {
      clearVisualFocusDom();
      return;
    }
    applyVisualFocusDom([ctx.cellKey]);
  }, [
    isEditable,
    ctx,
    axisFocus,
    lineDragVisual,
    floatingViewportNonce,
    editor,
    applyVisualFocusDom,
    clearVisualFocusDom,
    containerRef,
  ]);

  React.useEffect(
    () => () => {
      clearVisualFocusDom();
    },
    [clearVisualFocusDom],
  );

  React.useEffect(() => {
    if (!isEditable) return;
    const root = editor.getRootElement();
    if (!root) return;
    const onPointerDownCapture = (e: PointerEvent) => {
      if (e.button !== 0) return;
      if (!axisFocusRef.current) return;
      const el = e.target;
      if (!(el instanceof HTMLElement)) return;
      const cell = el.closest("td, th");
      if (!cell || !root.contains(cell)) return;
      setAxisFocus(null);
    };
    root.addEventListener("pointerdown", onPointerDownCapture, true);
    return () => root.removeEventListener("pointerdown", onPointerDownCapture, true);
  }, [editor, isEditable]);

  const resolveHoverTable = React.useCallback(
    (clientX: number, clientY: number) => {
      let next: string | null = null;
      const container = containerRef.current;
      if (container && isEditable) {
        const el = document.elementFromPoint(clientX, clientY);
        const colStrip = el?.closest("[data-rt-table-col-resize-hit]");
        if (colStrip) {
          const tk = colStrip.getAttribute("data-table-key");
          if (tk) next = tk;
        }
        if (next === null) {
          const rowStrip = el?.closest("[data-rt-table-row-resize-hit]");
          if (rowStrip) {
            const tk = rowStrip.getAttribute("data-table-key");
            if (tk) next = tk;
          }
        }
        if (next === null && el && container.contains(el) && !el.closest('[data-floating-root="true"]')) {
          const blockKey = pickTableBlockKeyAtViewportPoint(clientX, clientY, editor, container);
          editor.getEditorState().read(() => {
            const n = blockKey ? $getNodeByKey(blockKey as never) : null;
            if (n && $isTableNode(n as never)) next = blockKey;
          });
        }
      }
      hoverTableKeySyncRef.current = next;
      setHoverTableKey(next);
    },
    [containerRef, editor, isEditable],
  );

  const resolveAddStripPointer = React.useCallback(
    (clientX: number, clientY: number) => {
      const container = containerRef.current;
      if (!container || !isEditable) {
        stripStickyTableKeyRef.current = null;
        setAddStripPointer({ tableKey: null, row: false, col: false });
        return;
      }

      const hoverTk = hoverTableKeySyncRef.current;
      if (hoverTk) stripStickyTableKeyRef.current = hoverTk;

      const tryHit = (tk: string): { row: boolean; col: boolean } | null => {
        const r = readTableRectByTableKey(editor, tk);
        if (!r) return null;
        const hitRow = pointHitsTableAddRowStrip(clientX, clientY, r);
        const hitCol = pointHitsTableAddColStrip(clientX, clientY, r);
        if (!hitRow && !hitCol) return null;
        return { row: hitRow, col: hitCol };
      };

      const order: string[] = [];
      if (hoverTk) order.push(hoverTk);
      const sticky = stripStickyTableKeyRef.current;
      if (sticky && sticky !== hoverTk) order.push(sticky);

      let tableKey: string | null = null;
      let row = false;
      let col = false;
      for (const tk of order) {
        const hit = tryHit(tk);
        if (hit) {
          tableKey = tk;
          row = hit.row;
          col = hit.col;
          break;
        }
      }
      if (!tableKey) {
        const keys =
          tableKeysCacheRef.current.length > 0 ? tableKeysCacheRef.current : collectTableNodeKeys(editor);
        tableKeysCacheRef.current = keys;
        for (const tk of keys) {
          if (order.includes(tk)) continue;
          const hit = tryHit(tk);
          if (hit) {
            tableKey = tk;
            row = hit.row;
            col = hit.col;
            stripStickyTableKeyRef.current = tk;
            break;
          }
        }
      }

      if (!row && !col) {
        if (!hoverTk) stripStickyTableKeyRef.current = null;
        setAddStripPointer({ tableKey: null, row: false, col: false });
        return;
      }
      setAddStripPointer({ tableKey, row, col });
    },
    [containerRef, editor, isEditable],
  );

  const scheduleResolveHoverTable = React.useCallback(
    (clientX: number, clientY: number) => {
      lastPointerRef.current = { x: clientX, y: clientY };
      if (hoverRafRef.current) return;
      hoverRafRef.current = window.requestAnimationFrame(() => {
        hoverRafRef.current = 0;
        resolveHoverTable(clientX, clientY);
        resolveAddStripPointer(clientX, clientY);
      });
    },
    [resolveHoverTable, resolveAddStripPointer],
  );

  React.useEffect(() => {
    if (!isEditable) {
      hoverTableKeySyncRef.current = null;
      stripStickyTableKeyRef.current = null;
      tableKeysCacheRef.current = [];
      setHoverTableKey(null);
      setAddStripPointer({ tableKey: null, row: false, col: false });
      lastPointerRef.current = null;
      return;
    }
    const onMove = (e: PointerEvent) => {
      scheduleResolveHoverTable(e.clientX, e.clientY);
    };
    document.addEventListener("pointermove", onMove, { passive: true });
    return () => document.removeEventListener("pointermove", onMove);
  }, [isEditable, scheduleResolveHoverTable]);

  React.useEffect(() => {
    if (!isEditable) return;
    let raf = 0;
    return editor.registerUpdateListener(() => {
      tableKeysCacheRef.current = collectTableNodeKeys(editor);
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const p = lastPointerRef.current;
        if (p) {
          resolveHoverTable(p.x, p.y);
          resolveAddStripPointer(p.x, p.y);
        }
      });
    });
  }, [editor, isEditable, resolveHoverTable, resolveAddStripPointer]);

  React.useEffect(() => {
    if (!isEditable) return;
    return editor.registerCommand(
      INSERT_TABLE_COMMAND,
      () => {
        setTimeout(() => {
          editor.update(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return;
            const anchorNode = selection.anchor.getNode();
            let node: LexicalNode | null = anchorNode;
            while (node && !$isTableNode(node as never)) {
              node = node.getParent();
            }
            if (!node || !$isTableNode(node as never)) return;
            const tableNode = node as unknown as import("@lexical/table").TableNode;
            const firstRow = tableNode.getChildren()[0] as TableRowNode | undefined;
            if (!firstRow) return;
            const colCount = firstRow.getChildren().length;
            const colWidths = Array(colCount).fill(120);
            tableNode.setColWidths(colWidths);
          });
          requestAnimationFrame(() => {
            editor.getEditorState().read(() => {
              const selection = $getSelection();
              if (!$isRangeSelection(selection)) return;
              const anchorNode = selection.anchor.getNode();
              let node: LexicalNode | null = anchorNode;
              while (node && !$isTableNode(node as never)) {
                node = node.getParent();
              }
              if (!node) return;
              const rootEl = editor.getElementByKey(node.getKey());
              const tableEl =
                rootEl instanceof HTMLTableElement
                  ? rootEl
                  : (rootEl?.querySelector("table") as HTMLTableElement | null);
              if (tableEl) {
                tableEl.style.width = "";
                tableEl.style.maxWidth = "none";
              }
            });
          });
        }, 0);
        return false;
      },
      1,
    );
  }, [editor, isEditable]);

  const endRowLineDragVisual = React.useCallback(() => {
    const ref = rowDragRef.current;
    if (ref?.raf) cancelAnimationFrame(ref.raf);
    restoreSourceContentHidden(ref?.sourceContentHidden);
    resetBodyDragChromeStyles();
    rowLineDragGhostMountRef.current?.replaceChildren();
    rowLineDragGhostTableRef.current = null;
    setLineDragVisual((v) => (v?.kind === "row" ? null : v));
  }, []);

  const endColLineDragVisual = React.useCallback(() => {
    const ref = colDragRef.current;
    if (ref?.raf) cancelAnimationFrame(ref.raf);
    restoreSourceContentHidden(ref?.sourceContentHidden);
    resetBodyDragChromeStyles();
    colLineDragGhostMountRef.current?.replaceChildren();
    colLineDragGhostTableRef.current = null;
    setLineDragVisual((v) => (v?.kind === "col" ? null : v));
  }, []);

  const refresh = React.useCallback(() => {
    if (!isEditable) {
      setCtx(null);
      lastTableCtxRef.current = null;
      return;
    }
    const next = readTableCtx(editor);
    if (next) lastTableCtxRef.current = next;

    const stickChrome =
      axisFocusRef.current !== null ||
      rowMenuOpenRef.current ||
      colMenuOpenRef.current ||
      rowDragRef.current !== null ||
      colDragRef.current !== null ||
      resizeRef.current !== null ||
      rowResizeRef.current !== null;

    let display: TableCtx | null = next;
    if (!next && stickChrome && lastTableCtxRef.current) {
      const rebased = readTableCtxByCellKey(editor, lastTableCtxRef.current.cellKey);
      if (rebased) {
        lastTableCtxRef.current = rebased;
        display = rebased;
      } else {
        lastTableCtxRef.current = null;
        display = null;
        setAxisFocus(null);
        setRowMenuOpen(false);
        setColMenuOpen(false);
        if (rowDragRef.current) {
          endRowLineDragVisual();
          rowDragRef.current = null;
        }
        if (colDragRef.current) {
          endColLineDragVisual();
          colDragRef.current = null;
        }
      }
    } else if (next) {
      display = next;
    }

    setCtx(display);
  }, [editor, isEditable, endColLineDragVisual, endRowLineDragVisual]);

  React.useEffect(() => {
    if (!isEditable) return;
    const clearBodyCursorAfterFrame = () => {
      requestAnimationFrame(() => {
        if (!rowDragRef.current?.dragging && !colDragRef.current?.dragging) {
          resetBodyDragChromeStyles();
        }
      });
    };
    window.addEventListener("pointerup", clearBodyCursorAfterFrame);
    window.addEventListener("pointercancel", clearBodyCursorAfterFrame);
    return () => {
      window.removeEventListener("pointerup", clearBodyCursorAfterFrame);
      window.removeEventListener("pointercancel", clearBodyCursorAfterFrame);
    };
  }, [isEditable]);

  React.useEffect(() => {
    if (!isEditable) return;
    let raf = 0;
    return editor.registerUpdateListener(() => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        refresh();
      });
    });
  }, [editor, isEditable, refresh]);

  const resyncLineDragGeometry = React.useCallback(() => {
    const rr = rowDragRef.current;
    if (rr?.dragging && rr.cellKey) {
      const cellEl = editor.getElementByKey(rr.cellKey);
      if (!cellEl?.isConnected) return;
      const tr = cellEl.closest("tr");
      const root = editor.getElementByKey(rr.tableKey);
      const tableDom =
        root instanceof HTMLTableElement
          ? root
          : (root?.querySelector("table") as HTMLTableElement | null);
      if (!tr?.isConnected || !tableDom?.isConnected) return;
      const rowBox = toDragBox(tr.getBoundingClientRect());
      const tableBox = toDragBox(tableDom.getBoundingClientRect());
      rr.rowBox = rowBox;
      rr.tableBox = tableBox;
      rr.sourceTr = tr;
      rr.tableDom = tableDom;
      rr.minDy = tableBox.top - rowBox.top;
      rr.maxDy = tableBox.bottom - rowBox.bottom;
      const rawDy = rr.lastClientY - rr.startY;
      const d = clamp(rawDy, rr.minDy, rr.maxDy);
      rr.pendingDy = d;
      const dropLine = computeRowDropLine(tableDom, rr.fromIndex, d, rowBox);
      setLineDragVisual({ kind: "row", offsetY: d, row: rowBox, table: tableBox, dropLine });
    }
    const cr = colDragRef.current;
    if (cr?.dragging && cr.cellKey) {
      const cellEl = editor.getElementByKey(cr.cellKey);
      if (!cellEl?.isConnected) return;
      const root = editor.getElementByKey(cr.tableKey);
      const tableDom =
        root instanceof HTMLTableElement
          ? root
          : (root?.querySelector("table") as HTMLTableElement | null);
      if (!tableDom?.isConnected) return;
      const fr = tableDom.rows[0];
      const topCell = fr?.cells[cr.fromIndex];
      if (!topCell?.isConnected) return;
      const colBox = toDragBox(topCell.getBoundingClientRect());
      const tableBox = toDragBox(tableDom.getBoundingClientRect());
      cr.colBox = colBox;
      cr.tableBox = tableBox;
      cr.tableDom = tableDom;
      cr.minDx = tableBox.left - colBox.left;
      cr.maxDx = tableBox.right - colBox.right;
      const colCount = fr?.cells.length ?? cr.colCount ?? 0;
      cr.colCount = colCount;
      const rawDx = cr.lastClientX - cr.startX;
      const d = clamp(rawDx, cr.minDx, cr.maxDx);
      cr.pendingDx = d;
      const dropLine =
        colCount > 0 ? computeColDropLine(tableDom, cr.fromIndex, d, colBox, colCount) : null;
      setLineDragVisual({ kind: "col", offsetX: d, col: colBox, table: tableBox, dropLine });
    }
  }, [editor]);

  React.useLayoutEffect(() => {
    scrollSyncRef.current = () => {
      if (rowMenuOpenRef.current || colMenuOpenRef.current) {
        closeAllAxisMenusRef.current();
      }
      resyncLineDragGeometry();
      refresh();
      const p = lastPointerRef.current;
      if (p) resolveHoverTable(p.x, p.y);
      bumpFloatingClipLayout();
    };
  }, [refresh, resyncLineDragGeometry, resolveHoverTable]);

  React.useEffect(() => {
    if (!isEditable) return;
    const el = containerRef.current;
    const sync = () => scrollSyncRef.current();
    if (el) el.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("scroll", sync, { capture: true, passive: true });
    window.addEventListener("resize", sync);
    const vv = window.visualViewport;
    vv?.addEventListener("scroll", sync, { passive: true });
    vv?.addEventListener("resize", sync);

    let mo: MutationObserver | null = null;
    const attachWrappersUnder = (root: HTMLElement) => {
      root.querySelectorAll(".rt-table-scroll-wrapper").forEach((node) => {
        if (tableWrapperScrollAttachedRef.current.has(node)) return;
        tableWrapperScrollAttachedRef.current.add(node);
        node.addEventListener("scroll", sync, { passive: true });
      });
    };
    const bindMo = (root: HTMLElement) => {
      mo?.disconnect();
      mo = new MutationObserver(() => attachWrappersUnder(root));
      mo.observe(root, { childList: true, subtree: true });
      attachWrappersUnder(root);
    };

    const removeRootListener = editor.registerRootListener((rootEl: HTMLElement | null) => {
      mo?.disconnect();
      mo = null;
      if (rootEl) bindMo(rootEl);
    });

    return () => {
      removeRootListener();
      mo?.disconnect();
      if (el) el.removeEventListener("scroll", sync);
      window.removeEventListener("scroll", sync, true);
      window.removeEventListener("resize", sync);
      vv?.removeEventListener("scroll", sync);
      vv?.removeEventListener("resize", sync);
    };
  }, [editor, isEditable, containerRef]);

  const restoreRowAxisAfterReorder = React.useCallback(
    (cellKey: string, rowIndexFallback?: number) => {
      const c = lastTableCtxRef.current;
      const commit = (nk: TableCtx | null) => {
        if (nk) {
          lastTableCtxRef.current = nk;
          flushSync(() => {
            setCtx(nk);
            setAxisFocus({ kind: "row", tableKey: nk.tableKey, rowIndex: nk.rowIndex });
          });
          return;
        }
        if (!c?.simple) return;
        if (rowIndexFallback !== undefined) {
          const patched: TableCtx = { ...c, rowIndex: rowIndexFallback };
          lastTableCtxRef.current = patched;
          flushSync(() => {
            setCtx(patched);
            setAxisFocus({ kind: "row", tableKey: c.tableKey, rowIndex: rowIndexFallback });
          });
          return;
        }
        lastTableCtxRef.current = c;
        flushSync(() => {
          setCtx(c);
          setAxisFocus({ kind: "row", tableKey: c.tableKey, rowIndex: c.rowIndex });
        });
      };
      const nk0 = readTableCtxByCellKey(editor, cellKey);
      if (nk0) commit(nk0);
      else
        requestAnimationFrame(() => {
          commit(readTableCtxByCellKey(editor, cellKey) ?? null);
        });
    },
    [editor],
  );

  const restoreColAxisAfterReorder = React.useCallback(
    (cellKey: string, colIndexFallback?: number) => {
      const c = lastTableCtxRef.current;
      const commit = (nk: TableCtx | null) => {
        if (nk) {
          lastTableCtxRef.current = nk;
          flushSync(() => {
            setCtx(nk);
            setAxisFocus({ kind: "col", tableKey: nk.tableKey, colIndex: nk.colIndex });
          });
          return;
        }
        if (!c?.simple) return;
        if (colIndexFallback !== undefined) {
          const patched: TableCtx = { ...c, colIndex: colIndexFallback };
          lastTableCtxRef.current = patched;
          flushSync(() => {
            setCtx(patched);
            setAxisFocus({ kind: "col", tableKey: c.tableKey, colIndex: colIndexFallback });
          });
          return;
        }
        lastTableCtxRef.current = c;
        flushSync(() => {
          setCtx(c);
          setAxisFocus({ kind: "col", tableKey: c.tableKey, colIndex: c.colIndex });
        });
      };
      const nk0 = readTableCtxByCellKey(editor, cellKey);
      if (nk0) commit(nk0);
      else
        requestAnimationFrame(() => {
          commit(readTableCtxByCellKey(editor, cellKey) ?? null);
        });
    },
    [editor],
  );

  const withTableSelection = React.useCallback(
    (fn: (cell: TableCellNode) => void) => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        const cell = $getTableCellNodeFromLexicalNode(selection.anchor.getNode());
        if (!cell) return;
        fn(cell);
      });
      editor.focus();
    },
    [editor],
  );

  const copyRow = React.useCallback(() => {
    withTableSelection((cell) => {
      const rowIndex = $getTableRowIndexFromTableCellNode(cell);
      const inserted = $insertTableRowAtSelection(true);
      if (!inserted) return;
      const [, , table] = $getNodeTriplet(cell);
      const sourceRow = table.getChildren()[rowIndex] as TableRowNode | undefined;
      const targetRow = table.getChildren()[rowIndex + 1] as TableRowNode | undefined;
      if (!sourceRow || !targetRow) return;
      const srcCells = sourceRow.getChildren();
      const dstCells = targetRow.getChildren();
      for (let i = 0; i < Math.min(srcCells.length, dstCells.length); i++) {
        const srcText = srcCells[i].getTextContent();
        const dstCell = dstCells[i] as TableCellNode;
        dstCell.clear();
        const p = $createParagraphNode();
        if (srcText.length > 0) p.append($createTextNode(srcText));
        dstCell.append(p);
      }
    });
  }, [withTableSelection]);

  const copyColumn = React.useCallback(() => {
    withTableSelection((cell) => {
      const [, , table] = $getNodeTriplet(cell);
      if (!$isSimpleTable(table)) {
        $insertTableColumnAtSelection(true);
        return;
      }
      const colIndex = $getTableColumnIndexFromTableCellNode(cell);
      const insertedCell = $insertTableColumnAtSelection(true);
      if (!insertedCell) return;
      const nextCol = colIndex + 1;
      const rows = table.getChildren() as TableRowNode[];
      for (const row of rows) {
        const cells = row.getChildren() as TableCellNode[];
        const src = cells[colIndex];
        const dst = cells[nextCol];
        if (!src || !dst) continue;
        const srcText = src.getTextContent();
        dst.clear();
        const p = $createParagraphNode();
        if (srcText.length > 0) p.append($createTextNode(srcText));
        dst.append(p);
      }
    });
  }, [withTableSelection]);

  const moveRowBy = React.useCallback(
    (delta: number) => {
      let cellKeyOut: string | null = null;
      let moved = false;
      let toIdx = 0;
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        const cell = $getTableCellNodeFromLexicalNode(selection.anchor.getNode());
        if (!cell) return;
        cellKeyOut = cell.getKey();
        const [, , table] = $getNodeTriplet(cell);
        if (!$isSimpleTable(table)) return;
        const from = $getTableRowIndexFromTableCellNode(cell);
        const to = from + delta;
        const n = table.getChildren().length;
        if (to < 0 || to >= n) return;
        $moveTableRow(table, from, to);
        moved = true;
        toIdx = to;
        $placeCaretAtStartOfTableCell(cell);
      });
      editor.focus();
      if (moved && cellKeyOut) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            restoreRowAxisAfterReorder(cellKeyOut!, toIdx);
          });
        });
      }
    },
    [editor, restoreRowAxisAfterReorder],
  );

  const moveColBy = React.useCallback(
    (delta: number) => {
      let cellKeyOut: string | null = null;
      let moved = false;
      let toIdx = 0;
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        const cell = $getTableCellNodeFromLexicalNode(selection.anchor.getNode());
        if (!cell) return;
        cellKeyOut = cell.getKey();
        const [, , table] = $getNodeTriplet(cell);
        if (!$isSimpleTable(table)) return;
        const from = $getTableColumnIndexFromTableCellNode(cell);
        const to = from + delta;
        const firstRow = table.getChildren()[0] as TableRowNode | undefined;
        const colCount = firstRow ? firstRow.getChildren().length : 0;
        if (to < 0 || to >= colCount) return;
        $moveTableColumn(table, from, to);
        moved = true;
        toIdx = to;
        $placeCaretAtStartOfTableCell(cell);
      });
      editor.focus();
      if (moved && cellKeyOut) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            restoreColAxisAfterReorder(cellKeyOut!, toIdx);
          });
        });
      }
    },
    [editor, restoreColAxisAfterReorder],
  );

  const insertRowAtEndForTable = React.useCallback(
    (tableKey: string) => {
      editor.update(() => {
        const n = $getNodeByKey(tableKey as never);
        if (!n || !$isTableNode(n)) return;
        const rows = n.getChildren() as TableRowNode[];
        const lastRow = rows[rows.length - 1];
        if (!lastRow) return;
        const rowCells = lastRow.getChildren() as TableCellNode[];
        const anchorCell = rowCells[0];
        if (anchorCell) {
          $selectTableCellAsRange(anchorCell);
          $insertTableRowAtSelection(true);
        }
      });
      editor.focus();
    },
    [editor],
  );

  const insertColAtEndForTable = React.useCallback(
    (tableKey: string) => {
      editor.update(() => {
        const n = $getNodeByKey(tableKey as never);
        if (!n || !$isTableNode(n)) return;
        const firstRow = n.getChildren()[0] as TableRowNode | undefined;
        if (!firstRow) return;
        const rowCells = firstRow.getChildren() as TableCellNode[];
        const lastInRow = rowCells.length > 0 ? rowCells[rowCells.length - 1] : undefined;
        if (lastInRow) {
          $selectTableCellAsRange(lastInRow);
          $insertTableColumnAtSelection(true);
        }
      });
      editor.focus();
    },
    [editor],
  );

  const insertRowAtEnd = React.useCallback(() => {
    const tk = addStripPointerRef.current.tableKey;
    if (tk) insertRowAtEndForTable(tk);
  }, [insertRowAtEndForTable]);

  const insertColAtEnd = React.useCallback(() => {
    const tk = addStripPointerRef.current.tableKey;
    if (tk) insertColAtEndForTable(tk);
  }, [insertColAtEndForTable]);

  const closeRowAxisMenu = React.useCallback(() => {
    setRowMenuOpen(false);
    setRowMenuAnchor(null);
    setAxisFocus((p) => (p?.kind === "row" ? null : p));
  }, []);

  const closeColAxisMenu = React.useCallback(() => {
    setColMenuOpen(false);
    setColMenuAnchor(null);
    setAxisFocus((p) => (p?.kind === "col" ? null : p));
  }, []);

  const openRowMenuFromGrip = React.useCallback((anchor: DOMRect) => {
    setColMenuOpen(false);
    setColMenuAnchor(null);
    setRowMenuAnchor(snapTableAxisMenuAnchor(anchor));
    setRowMenuOpen(true);
    const c = ctxRef.current ?? lastTableCtxRef.current;
    if (c) {
      setAxisFocus({ kind: "row", tableKey: c.tableKey, rowIndex: c.rowIndex });
      if (!ctxRef.current) setCtx(c);
    }
  }, []);

  const openColMenuFromGrip = React.useCallback((anchor: DOMRect) => {
    setRowMenuOpen(false);
    setRowMenuAnchor(null);
    setColMenuAnchor(snapTableAxisMenuAnchor(anchor));
    setColMenuOpen(true);
    const c = ctxRef.current ?? lastTableCtxRef.current;
    if (c) {
      setAxisFocus({ kind: "col", tableKey: c.tableKey, colIndex: c.colIndex });
      if (!ctxRef.current) setCtx(c);
    }
  }, []);

  React.useLayoutEffect(() => {
    closeAllAxisMenusRef.current = () => {
      setRowMenuOpen(false);
      setRowMenuAnchor(null);
      setColMenuOpen(false);
      setColMenuAnchor(null);
      setAxisFocus(null);
    };
  });

  React.useEffect(() => {
    if (!rowMenuOpen && !colMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeAllAxisMenusRef.current();
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [rowMenuOpen, colMenuOpen]);

  const onRowGripPointerDown = React.useCallback(
    (e: React.PointerEvent) => {
      if (!ctx || e.button !== 0) return;
      const hadRowMenu = rowMenuOpenRef.current;
      closeColAxisMenu();
      setRowMenuOpen(false);
      setRowMenuAnchor(null);
      if (hadRowMenu) setAxisFocus((p) => (p?.kind === "row" ? null : p));
      rowDragRef.current = {
        pointerId: e.pointerId,
        startY: e.clientY,
        lastClientY: e.clientY,
        dragging: false,
        fromIndex: ctx.rowIndex,
        tableKey: ctx.tableKey,
      };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [ctx, closeColAxisMenu],
  );

  const onRowGripPointerMove = React.useCallback(
    (e: React.PointerEvent) => {
      const ref = rowDragRef.current;
      if (!ref || e.pointerId !== ref.pointerId) return;
      ref.lastClientY = e.clientY;
      const rawDy = e.clientY - ref.startY;
      if (!ref.dragging) {
        if (Math.abs(rawDy) <= DRAG_THRESHOLD_PX) return;
        const c = ctxRef.current ?? lastTableCtxRef.current;
        if (!c?.simple) return;
        ref.dragging = true;
        setRowMenuOpen(false);
        setAxisFocus(null);
        const rowBox = toDragBox(c.rowRect);
        const tableBox = toDragBox(c.tableRect);
        ref.rowBox = rowBox;
        ref.tableBox = tableBox;
        ref.minDy = tableBox.top - rowBox.top;
        ref.maxDy = tableBox.bottom - rowBox.bottom;
        ref.cellKey = c.cellKey;
        const cellEl = editor.getElementByKey(c.cellKey);
        const tr = cellEl?.closest("tr") as HTMLTableRowElement | null;
        ref.sourceTr = tr ?? null;
        const root = editor.getElementByKey(c.tableKey);
        const tableDom =
          root instanceof HTMLTableElement
            ? root
            : (root?.querySelector("table") as HTMLTableElement | null);
        ref.tableDom = tableDom ?? null;
        ref.sourceContentHidden = tr ? hideTableRowCellContents(tr) : [];
        if (rowLineDragGhostTableRef.current) {
          rowLineDragGhostTableRef.current.remove();
          rowLineDragGhostTableRef.current = null;
        }
        if (tr) {
          rowLineDragGhostTableRef.current = buildRowDragGhostTable(tr);
        }
        document.body.style.cursor = "grabbing";
        document.body.style.userSelect = "none";
        const dy0 = clamp(rawDy, ref.minDy, ref.maxDy);
        const dropLine =
          tableDom ? computeRowDropLine(tableDom, ref.fromIndex, dy0, rowBox) : null;
        setLineDragVisual({ kind: "row", offsetY: dy0, row: rowBox, table: tableBox, dropLine });
      }
      if (!ref.dragging || ref.rowBox === undefined || ref.tableBox === undefined) return;
      const dy = clamp(rawDy, ref.minDy!, ref.maxDy!);
      ref.pendingDy = dy;
      if (ref.raf) return;
      ref.raf = window.requestAnimationFrame(() => {
        ref.raf = 0;
        if (!rowDragRef.current?.dragging) return;
        const d = rowDragRef.current.pendingDy ?? 0;
        const rb = rowDragRef.current.rowBox!;
        const tb = rowDragRef.current.tableBox!;
        const td = rowDragRef.current.tableDom;
        const dropLine = td ? computeRowDropLine(td, rowDragRef.current.fromIndex, d, rb) : null;
        setLineDragVisual({ kind: "row", offsetY: d, row: rb, table: tb, dropLine });
      });
    },
    [editor],
  );

  const onRowGripPointerUp = React.useCallback(
    (e: React.PointerEvent) => {
      const ref = rowDragRef.current;
      if (!ref || e.pointerId !== ref.pointerId) return;
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      const startY = ref.startY;
      const dy = e.clientY - startY;
      const wasDragging = ref.dragging;
      const rowBox = ref.rowBox;
      const minDy = ref.minDy;
      const maxDy = ref.maxDy;
      const tableKeySnap = ref.tableKey;
      const fromIndexSnap = ref.fromIndex;
      const tableDomSnap = ref.tableDom;
      const cellKeySnap = ref.cellKey;
      endRowLineDragVisual();
      rowDragRef.current = null;
      const c = ctxRef.current ?? lastTableCtxRef.current;
      const treatAsClick = Math.abs(dy) < DRAG_THRESHOLD_PX * 3 && !wasDragging;
      if (treatAsClick) {
        openRowMenuFromGrip((e.currentTarget as HTMLElement).getBoundingClientRect());
        return;
      }
      suppressRowClickRef.current = true;
      if (!c?.simple) return;
      if (!wasDragging || rowBox === undefined || minDy === undefined || maxDy === undefined) return;
      if (!tableDomSnap) return;
      const clamped = clamp(dy, minDy, maxDy);
      const n = tableDomSnap.rows.length;

      const applyRowAxisChrome = (rowIndexFallback?: number) => {
        if (!c?.simple || c.tableKey !== tableKeySnap) return;
        restoreRowAxisAfterReorder(cellKeySnap ?? c.cellKey, rowIndexFallback);
      };

      const resolved = resolveRowLineDrag(tableDomSnap, fromIndexSnap, clamped, rowBox, n);
      if (!resolved) {
        applyRowAxisChrome();
        return;
      }
      const toIndex = rowMoveToIndex(fromIndexSnap, resolved.insertSlot, n);
      if (toIndex === fromIndexSnap) {
        applyRowAxisChrome();
        return;
      }
      let moved = false;
      editor.update(() => {
        const table = $getNodeByKey(tableKeySnap as never);
        if (!$isTableNode(table as never) || !$isSimpleTable(table as never)) return;
        const t = table as unknown as import("@lexical/table").TableNode;
        if (fromIndexSnap < 0 || fromIndexSnap >= t.getChildren().length) return;
        $moveTableRow(t, fromIndexSnap, toIndex);
        moved = true;
        const k = cellKeySnap ?? c?.cellKey;
        if (k) {
          const rawNode = $getNodeByKey(k as never);
          if (rawNode) {
            let cellNode = $getTableCellNodeFromLexicalNode(rawNode as never);
            if (!cellNode && rawNode.getType() === "tablecell") cellNode = rawNode as TableCellNode;
            if (cellNode) $placeCaretAtStartOfTableCell(cellNode);
          }
        }
      });
      if (moved) {
        applyRowAxisChrome(toIndex);
      } else {
        applyRowAxisChrome();
      }
    },
    [editor, endRowLineDragVisual, openRowMenuFromGrip, restoreRowAxisAfterReorder],
  );

  const onColGripPointerDown = React.useCallback(
    (e: React.PointerEvent) => {
      if (!ctx || e.button !== 0) return;
      const hadColMenu = colMenuOpenRef.current;
      closeRowAxisMenu();
      setColMenuOpen(false);
      setColMenuAnchor(null);
      if (hadColMenu) setAxisFocus((p) => (p?.kind === "col" ? null : p));
      colDragRef.current = {
        pointerId: e.pointerId,
        startX: e.clientX,
        lastClientX: e.clientX,
        dragging: false,
        fromIndex: ctx.colIndex,
        tableKey: ctx.tableKey,
      };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    },
    [ctx, closeRowAxisMenu],
  );

  const onColGripPointerMove = React.useCallback(
    (e: React.PointerEvent) => {
      const ref = colDragRef.current;
      if (!ref || e.pointerId !== ref.pointerId) return;
      ref.lastClientX = e.clientX;
      const rawDx = e.clientX - ref.startX;
      if (!ref.dragging) {
        if (Math.abs(rawDx) <= DRAG_THRESHOLD_PX) return;
        const c = ctxRef.current ?? lastTableCtxRef.current;
        if (!c?.simple) return;
        ref.dragging = true;
        setColMenuOpen(false);
        setAxisFocus(null);
        const colBox = toDragBox(c.columnTopRect);
        const tableBox = toDragBox(c.tableRect);
        ref.colBox = colBox;
        ref.tableBox = tableBox;
        ref.minDx = tableBox.left - colBox.left;
        ref.maxDx = tableBox.right - colBox.right;
        ref.cellKey = c.cellKey;
        const root = editor.getElementByKey(c.tableKey);
        const tableDom =
          root instanceof HTMLTableElement
            ? root
            : (root?.querySelector("table") as HTMLTableElement | null);
        ref.tableDom = tableDom ?? null;
        ref.colCount = tableDom?.rows[0]?.cells.length ?? 0;
        ref.sourceContentHidden = tableDom ? hideTableColumnCellContents(tableDom, c.colIndex) : [];
        if (colLineDragGhostTableRef.current) {
          colLineDragGhostTableRef.current.remove();
          colLineDragGhostTableRef.current = null;
        }
        if (tableDom) {
          colLineDragGhostTableRef.current = buildColDragGhostTable(tableDom, c.colIndex);
        }
        document.body.style.cursor = "grabbing";
        document.body.style.userSelect = "none";
        const dx0 = clamp(rawDx, ref.minDx, ref.maxDx);
        const dropLine =
          tableDom && ref.colCount
            ? computeColDropLine(tableDom, ref.fromIndex, dx0, colBox, ref.colCount)
            : null;
        setLineDragVisual({ kind: "col", offsetX: dx0, col: colBox, table: tableBox, dropLine });
      }
      if (!ref.dragging || ref.colBox === undefined || ref.tableBox === undefined) return;
      const dx = clamp(rawDx, ref.minDx!, ref.maxDx!);
      ref.pendingDx = dx;
      if (ref.raf) return;
      ref.raf = window.requestAnimationFrame(() => {
        ref.raf = 0;
        if (!colDragRef.current?.dragging) return;
        const d = colDragRef.current.pendingDx ?? 0;
        const cb = colDragRef.current.colBox!;
        const tb = colDragRef.current.tableBox!;
        const td = colDragRef.current.tableDom;
        const cc = colDragRef.current.colCount ?? 0;
        const dropLine =
          td && cc > 0 ? computeColDropLine(td, colDragRef.current.fromIndex, d, cb, cc) : null;
        setLineDragVisual({ kind: "col", offsetX: d, col: cb, table: tb, dropLine });
      });
    },
    [editor],
  );

  const onColGripPointerUp = React.useCallback(
    (e: React.PointerEvent) => {
      const ref = colDragRef.current;
      if (!ref || e.pointerId !== ref.pointerId) return;
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
      const startX = ref.startX;
      const dx = e.clientX - startX;
      const wasDragging = ref.dragging;
      const colBox = ref.colBox;
      const minDx = ref.minDx;
      const maxDx = ref.maxDx;
      const tableKeySnap = ref.tableKey;
      const fromIndexSnap = ref.fromIndex;
      const tableDomSnap = ref.tableDom;
      const colCountSnap = ref.colCount ?? 0;
      const cellKeySnap = ref.cellKey;
      endColLineDragVisual();
      colDragRef.current = null;
      const c = ctxRef.current ?? lastTableCtxRef.current;
      const treatAsClick = Math.abs(dx) < DRAG_THRESHOLD_PX * 3 && !wasDragging;
      if (treatAsClick) {
        openColMenuFromGrip((e.currentTarget as HTMLElement).getBoundingClientRect());
        return;
      }
      suppressColClickRef.current = true;
      if (!c?.simple) return;
      if (!wasDragging || colBox === undefined || minDx === undefined || maxDx === undefined) return;
      if (!tableDomSnap || colCountSnap <= 0) return;
      const clamped = clamp(dx, minDx, maxDx);

      const applyColAxisChrome = (colIndexFallback?: number) => {
        if (!c?.simple || c.tableKey !== tableKeySnap) return;
        restoreColAxisAfterReorder(cellKeySnap ?? c.cellKey, colIndexFallback);
      };

      const resolved = resolveColLineDrag(tableDomSnap, fromIndexSnap, clamped, colBox, colCountSnap);
      if (!resolved) {
        applyColAxisChrome();
        return;
      }
      const toIndex = colMoveToIndex(fromIndexSnap, resolved.insertSlot, colCountSnap);
      if (toIndex === fromIndexSnap) {
        applyColAxisChrome();
        return;
      }
      let moved = false;
      editor.update(() => {
        const table = $getNodeByKey(tableKeySnap as never);
        if (!$isTableNode(table as never) || !$isSimpleTable(table as never)) return;
        const t = table as unknown as import("@lexical/table").TableNode;
        const firstRow = t.getChildren()[0] as TableRowNode | undefined;
        const colCount = firstRow ? firstRow.getChildren().length : 0;
        if (fromIndexSnap < 0 || fromIndexSnap >= colCount || toIndex < 0 || toIndex >= colCount) return;
        $moveTableColumn(t, fromIndexSnap, toIndex);
        moved = true;
        const k = cellKeySnap ?? c?.cellKey;
        if (k) {
          const rawNode = $getNodeByKey(k as never);
          if (rawNode) {
            let cellNode = $getTableCellNodeFromLexicalNode(rawNode as never);
            if (!cellNode && rawNode.getType() === "tablecell") cellNode = rawNode as TableCellNode;
            if (cellNode) $placeCaretAtStartOfTableCell(cellNode);
          }
        }
      });
      if (moved) {
        applyColAxisChrome(toIndex);
      } else {
        applyColAxisChrome();
      }
    },
    [editor, endColLineDragVisual, openColMenuFromGrip, restoreColAxisAfterReorder],
  );

  const onResizePointerDown = React.useCallback(
    (tableKey: string, colIndex: number, e: React.PointerEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      setResizingColIndex(colIndex);
      let startWidths: number[] = [];
      editor.getEditorState().read(() => {
        const table = $getNodeByKey(tableKey as never);
        if (!$isTableNode(table as never)) return;
        const t = table as unknown as import("@lexical/table").TableNode;
        const prev = t.getColWidths() ?? [];
        startWidths = [...prev];
        const row = t.getChildren()[0] as TableRowNode | undefined;
        const n = row ? row.getChildren().length : 0;
        while (startWidths.length < n) startWidths.push(120);
      });
      if (startWidths.length === 0) {
        setResizingColIndex(null);
        return;
      }
      resizeRef.current = {
        pointerId: e.pointerId,
        colIndex,
        startX: e.clientX,
        startWidths,
        tableKey,
      };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [editor],
  );

  const onResizePointerMove = React.useCallback(
    (e: React.PointerEvent) => {
      const ref = resizeRef.current;
      if (!ref || e.pointerId !== ref.pointerId) return;
      const dx = e.clientX - ref.startX;
      const next = [...ref.startWidths];
      const i = ref.colIndex;
      if (i < 0 || i >= next.length) return;
      next[i] = Math.max(MIN_COL_WIDTH, Math.round((next[i] ?? 120) + dx));
      editor.update(() => {
        const table = $getNodeByKey(ref.tableKey as never);
        if (!$isTableNode(table as never)) return;
        (table as unknown as import("@lexical/table").TableNode).setColWidths(next);
      });
      ref.startX = e.clientX;
      ref.startWidths = next;
    },
    [editor],
  );

  const onResizePointerUp = React.useCallback((e: React.PointerEvent) => {
    const ref = resizeRef.current;
    if (ref && e.pointerId === ref.pointerId) {
      try {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
    resizeRef.current = null;
    setResizingColIndex(null);
  }, []);

  const onResizeLostPointerCapture = React.useCallback(() => {
    resizeRef.current = null;
    setResizingColIndex(null);
  }, []);

  const onRowResizePointerDown = React.useCallback(
    (tableKey: string, rowIndex: number, e: React.PointerEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      setResizingRowIndex(rowIndex);
      const startHeights: number[] = [];
      editor.getEditorState().read(() => {
        const table = $getNodeByKey(tableKey as never);
        if (!$isTableNode(table as never)) return;
        const t = table as unknown as import("@lexical/table").TableNode;
        for (const ch of t.getChildren()) {
          if (!$isTableRowNode(ch as never)) continue;
          const rowNode = ch as TableRowNode;
          let h = rowNode.getHeight();
          if (h == null || h <= 0) {
            const tr = editor.getElementByKey(rowNode.getKey());
            if (tr instanceof HTMLTableRowElement) {
              h = Math.round(tr.getBoundingClientRect().height);
            }
          }
          startHeights.push(Math.max(MIN_ROW_HEIGHT, h ?? MIN_ROW_HEIGHT));
        }
      });
      if (startHeights.length === 0 || rowIndex < 0 || rowIndex >= startHeights.length) {
        setResizingRowIndex(null);
        return;
      }
      rowResizeRef.current = {
        pointerId: e.pointerId,
        rowIndex,
        startY: e.clientY,
        startHeights,
        tableKey,
      };
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    },
    [editor],
  );

  const onRowResizePointerMove = React.useCallback(
    (e: React.PointerEvent) => {
      const ref = rowResizeRef.current;
      if (!ref || e.pointerId !== ref.pointerId) return;
      const dy = e.clientY - ref.startY;
      const next = [...ref.startHeights];
      const i = ref.rowIndex;
      if (i < 0 || i >= next.length) return;
      next[i] = Math.max(MIN_ROW_HEIGHT, Math.round((next[i] ?? MIN_ROW_HEIGHT) + dy));
      editor.update(() => {
        const table = $getNodeByKey(ref.tableKey as never);
        if (!$isTableNode(table as never)) return;
        const t = table as unknown as import("@lexical/table").TableNode;
        let ri = 0;
        for (const ch of t.getChildren()) {
          if (!$isTableRowNode(ch as never)) continue;
          if (ri === i) {
            (ch as TableRowNode).setHeight(next[i]);
            break;
          }
          ri += 1;
        }
      });
      ref.startY = e.clientY;
      ref.startHeights = next;
    },
    [editor],
  );

  const onRowResizePointerUp = React.useCallback((e: React.PointerEvent) => {
    const ref = rowResizeRef.current;
    if (ref && e.pointerId === ref.pointerId) {
      try {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    }
    rowResizeRef.current = null;
    setResizingRowIndex(null);
  }, []);

  const onRowResizeLostPointerCapture = React.useCallback(() => {
    rowResizeRef.current = null;
    setResizingRowIndex(null);
  }, []);

  const onRowGripLostPointerCapture = React.useCallback(() => {
    if (!rowDragRef.current) return;
    endRowLineDragVisual();
    rowDragRef.current = null;
  }, [endRowLineDragVisual]);

  const onColGripLostPointerCapture = React.useCallback(() => {
    if (!colDragRef.current) return;
    endColLineDragVisual();
    colDragRef.current = null;
  }, [endColLineDragVisual]);

  const onRowInsertAbove = React.useCallback(() => {
    withTableSelection(() => void $insertTableRowAtSelection(false));
  }, [withTableSelection]);

  const onRowInsertBelow = React.useCallback(() => {
    withTableSelection(() => void $insertTableRowAtSelection(true));
  }, [withTableSelection]);

  const onRowDelete = React.useCallback(() => {
    withTableSelection(() => $deleteTableRowAtSelection());
  }, [withTableSelection]);

  const onColInsertLeft = React.useCallback(() => {
    withTableSelection(() => void $insertTableColumnAtSelection(false));
  }, [withTableSelection]);

  const onColInsertRight = React.useCallback(() => {
    withTableSelection(() => void $insertTableColumnAtSelection(true));
  }, [withTableSelection]);

  const onColDelete = React.useCallback(() => {
    withTableSelection(() => $deleteTableColumnAtSelection());
  }, [withTableSelection]);

  const addStripLayouts = React.useMemo(() => {
    const tk = addStripPointer.tableKey;
    const clip = containerRef.current?.getBoundingClientRect() ?? null;
    if (!tk) return { rowStrip: null, colStrip: null };
    const tr = readTableRectByTableKey(editor, tk);
    const { rowStrip, colStrip } = computeAddStripLayoutsFromTableRect(tr, clip);
    return {
      rowStrip: addStripPointer.row ? rowStrip : null,
      colStrip: addStripPointer.col ? colStrip : null,
    };
  }, [addStripPointer, editor, floatingViewportNonce]);

  if (!isEditable || !portalEl) return null;

  const showHandles = !!ctx;
  const containerClip = containerRef.current?.getBoundingClientRect() ?? null;
  const rowGripStyleRaw = ctx ? buildRowGripStyle(ctx, lineDragVisual) : null;
  const colGripStyleRaw = ctx ? buildColGripStyle(ctx, lineDragVisual) : null;
  const rowGripStyle = clipFixedStyleToContainer(rowGripStyleRaw, containerClip);
  const colGripStyle = clipFixedStyleToContainer(colGripStyleRaw, containerClip);
  const axisOverlayGeom = ctx ?? (axisFocus ? lastTableCtxRef.current : null);
  const axisFocusOverlayStyleRaw = buildAxisFocusOverlayStyle(axisOverlayGeom, axisFocus, lineDragVisual);
  const axisFocusOverlayStyle = clipFixedStyleToContainer(axisFocusOverlayStyleRaw, containerClip);
  const columnResizeGuides = buildColumnResizeGuides(editor, hoverTableKey);
  const rowResizeGuides = buildRowResizeGuides(editor, hoverTableKey);
  const { rowStrip: rowAddStripLayout, colStrip: colAddStripLayout } = addStripLayouts;

  return createPortal(
    <TableInteractionPortals
      lineDragVisual={lineDragVisual}
      rowLineDragGhostMountRef={rowLineDragGhostMountRef}
      colLineDragGhostMountRef={colLineDragGhostMountRef}
      axisFocusOverlayStyle={axisFocusOverlayStyle}
      rowMenuOpen={rowMenuOpen}
      colMenuOpen={colMenuOpen}
      closeAllAxisMenusRef={closeAllAxisMenusRef}
      ctx={ctx}
      showHandles={showHandles}
      rowGripStyle={rowGripStyle}
      colGripStyle={colGripStyle}
      columnResizeGuides={columnResizeGuides}
      rowResizeGuides={rowResizeGuides}
      resizingColIndex={resizingColIndex}
      resizingRowIndex={resizingRowIndex}
      onResizePointerDown={onResizePointerDown}
      onResizePointerMove={onResizePointerMove}
      onResizePointerUp={onResizePointerUp}
      onResizeLostPointerCapture={onResizeLostPointerCapture}
      onRowResizePointerDown={onRowResizePointerDown}
      onRowResizePointerMove={onRowResizePointerMove}
      onRowResizePointerUp={onRowResizePointerUp}
      onRowResizeLostPointerCapture={onRowResizeLostPointerCapture}
      insertRowAtEnd={insertRowAtEnd}
      insertColAtEnd={insertColAtEnd}
      rowMenuAnchor={rowMenuAnchor}
      colMenuAnchor={colMenuAnchor}
      closeRowAxisMenu={closeRowAxisMenu}
      closeColAxisMenu={closeColAxisMenu}
      onRowInsertAbove={onRowInsertAbove}
      onRowInsertBelow={onRowInsertBelow}
      onRowCopy={copyRow}
      onRowMoveUp={() => moveRowBy(-1)}
      onRowMoveDown={() => moveRowBy(1)}
      onRowDelete={onRowDelete}
      onColInsertLeft={onColInsertLeft}
      onColInsertRight={onColInsertRight}
      onColCopy={copyColumn}
      onColMoveLeft={() => moveColBy(-1)}
      onColMoveRight={() => moveColBy(1)}
      onColDelete={onColDelete}
      onRowGripPointerDown={onRowGripPointerDown}
      onRowGripPointerMove={onRowGripPointerMove}
      onRowGripPointerUp={onRowGripPointerUp}
      onColGripPointerDown={onColGripPointerDown}
      onColGripPointerMove={onColGripPointerMove}
      onColGripPointerUp={onColGripPointerUp}
      onRowGripLostPointerCapture={onRowGripLostPointerCapture}
      onColGripLostPointerCapture={onColGripLostPointerCapture}
      suppressRowClickRef={suppressRowClickRef}
      suppressColClickRef={suppressColClickRef}
      rowAddStripLayout={rowAddStripLayout}
      colAddStripLayout={colAddStripLayout}
    />,
    portalEl,
  );
}
